import React, { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import PropTypes from 'prop-types';
import _orderBy from 'lodash/orderBy';
import _get from 'lodash/get';

import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import ButtonDisclosures from '../../../atoms/button-disclosures';
import CardModule from '../../shared/card-module';
import Icon from '../../../atoms/icon';
import ModalBasic from '../../../atoms/modal-basic';
import styles from './investment-holdings.module.scss';
import TableBasic from '../../../atoms/table-basic/index';
import useProfileCommonStore from '../../../../stores/profile/use-profile-common-store';
import format from '../../../../utils/format';
import { useAllHoldings } from '../../../../hooks/api';
import { ASSET_CLASSES } from '../../../../stores/profile/get-asset-class';
import Pagination from '../../../atoms/pagination';

const className = augmentClassName(styles);
const equityTableHeadersRawName = [
  'HoldingTicker',
  'HoldingName',
  'SharesHeldPrincipal',
  'MarketValue',
  'PctNetAssets'
];
const fixedIncomeTableHeadersRawName = [
  'HoldingName',
  'CouponRate',
  'MaturityDate',
  'SharesHeldPrincipal',
  'MarketValue',
  'PctNetAssets'
];
const multiAssetTableHeadersRawName = [
  'HoldingTicker',
  'HoldingName',
  'CouponRate',
  'MaturityDate',
  'SharesHeldPrincipal',
  'SharesHeldPrincipal2',
  'MarketValue',
  'PctNetAssets'
];
const InvestmentHoldings = ({ holdings, detailedHoldingsUrl, isFundNonProp = false }) => {
  const { apiConfigBase, commonState = {} } = useProfileCommonStore();
  const holdingsOrdered =
    holdings && holdings.length
      ? _orderBy(holdings.filter(Boolean), ['weightPercent'], ['desc'])
      : [{ name: '--', marketValue: 0, weightPercent: 0 }];
  const asOfDate = holdings && holdings.length ? holdings[0].asOfDate : null;

  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleShow = e => setIsModalVisible(true);

  const handleClose = e => setIsModalVisible(false);

  let allHoldingsData = [];
  let tableHeadersRawName = [];
  const hash = window.location.hash.substring(1);

  useEffect(() => {
    if (hash === 'detailedHoldings' && detailedHoldingsUrl) {
      setIsModalVisible(true);
    }
  }, [detailedHoldingsUrl, hash]); // eslint-disable-line react-hooks/exhaustive-deps

  const getTableHeader = assetClass => {
    switch (assetClass) {
      case ASSET_CLASSES.equities:
        tableHeadersRawName = equityTableHeadersRawName;
        return ['Ticker', 'Holding Name', '# of Shares', 'Market Value (USD)', 'Weight (%)'];
      case ASSET_CLASSES.fixedIncome:
      case ASSET_CLASSES.moneyMarket:
        tableHeadersRawName = fixedIncomeTableHeadersRawName;
        return [
          'Holding Name',
          'Coupon (%)',
          'Maturity Date',
          'Principal Amount',
          'Market Value (USD)',
          'Weight (%)'
        ];
      case ASSET_CLASSES.multiAsset:
        tableHeadersRawName = multiAssetTableHeadersRawName;
        return [
          'Ticker',
          'Holding Name',
          'Coupon (%)',
          'Maturity Date',
          'Principal Amount',
          '# of Shares',
          'Market Value (USD)',
          'Weight (%)'
        ];
      default:
        return [];
    }
  };
  const equityRow = (item, index) => (
    <tr data-key={`${item.HoldingName || ''}-${index}`} key={`${item.HoldingName || ''}-${index}`}>
      <td {...className('text-left', 'text-black', 'font-weight-bold')}>
        {item.HoldingTicker || '--'}
      </td>
      <td {...className('text-left')}>{item.HoldingName || '--'}</td>
      <td {...className('text-right', 'pr-4', 'font-weight-bold')}>{item.SharesHeldPrincipal}</td>
      <td {...className('text-right', 'pr-4', 'font-weight-bold')}>
        {item.MarketValue ? format.price(item.MarketValue, { formatString: '$0.0a' }, true) : '--'}
      </td>
      <td {...className('text-right', 'font-weight-bold')}>
        {item.PctNetAssets ? format.percent(item.PctNetAssets) : '--'}
      </td>
    </tr>
  );

  const fixedIncomeAndMMRow = (item, index) => (
    <tr data-key={`${item.HoldingName || ''}-${index}`} key={`${item.HoldingName || ''}-${index}`}>
      <td {...className('text-left')}>{item.HoldingName || '--'}</td>
      <td {...className('text-right', 'font-weight-bold')}>
        {item.CouponRate ? format.percent(item.CouponRate) : '--'}
      </td>
      <td {...className('text-right', 'font-weight-bold')}>{format.oaDate(item.MaturityDate)}</td>
      <td {...className('text-right', 'pr-4', 'font-weight-bold')}>
        {item.MaturityDate ? format.number(item.SharesHeldPrincipal) : '--'}
        {/* // Principal Amount */}
      </td>
      <td {...className('text-right', 'pr-4', 'font-weight-bold')}>
        {item.MarketValue ? format.price(item.MarketValue, { formatString: '$0.0a' }, true) : '--'}
      </td>
      <td {...className('text-right', 'font-weight-bold')}>
        {item.PctNetAssets ? format.percent(item.PctNetAssets) : '--'}
      </td>
    </tr>
  );

  const multiAssetRow = (item, index) => (
    <tr data-key={`${item.HoldingName || ''}-${index}`} key={`${item.HoldingName || ''}-${index}`}>
      <td {...className('text-left', 'text-black', 'font-weight-bold')}>
        {item.HoldingTicker || '--'}
      </td>
      <td {...className('text-left')}>{item.HoldingName || '--'}</td>
      <td {...className('text-right', 'font-weight-bold')}>
        {item.CouponRate ? format.percent(item.CouponRate) : '--'}
      </td>
      <td {...className('text-right', 'font-weight-bold')}>{format.oaDate(item.MaturityDate)}</td>
      <td {...className('text-right', 'pr-4', 'font-weight-bold')}>
        {item.MaturityDate ? '--' : format.number(item.SharesHeldPrincipal)}
        {/* Principal Amount */}
      </td>
      <td {...className('text-right', 'pr-4', 'font-weight-bold')}>
        {item.MaturityDate ? format.number(item.SharesHeldPrincipal) : '--'}
        {/* # of Shares */}
      </td>
      <td {...className('text-right', 'pr-4', 'font-weight-bold')}>
        {item.MarketValue ? format.price(item.MarketValue, { formatString: '$0.0a' }, true) : '--'}
      </td>
      <td {...className('text-right', 'font-weight-bold')}>
        {item.PctNetAssets ? format.percent(item.PctNetAssets) : '--'}
      </td>
    </tr>
  );

  const getTableRow = assetClass => {
    switch (assetClass) {
      case ASSET_CLASSES.equities:
        return equityRow;
      case ASSET_CLASSES.fixedIncome:
      case ASSET_CLASSES.moneyMarket:
        return fixedIncomeAndMMRow;
      case ASSET_CLASSES.multiAsset:
        return multiAssetRow;
      default:
        return equityRow;
    }
  };

  // Sorting
  const [sortBy, setSortBy] = useState('PctNetAssets');
  const [sortDir, setSortDir] = useState('desc');
  const allHoldingsInputs = {
    client: apiConfigBase?.client,
    xid: _get(commonState, 'xref.data.xid'),
    startRow: 1,
    numRows: 500,
    sortDir: sortDir || 'desc',
    sortBy: sortBy === 'SharesHeldPrincipal2' ? 'SharesHeldPrincipal' : sortBy || 'PctNetAssets'
  };
  const useAllHoldingsRequest = useAllHoldings(allHoldingsInputs);

  if (useAllHoldingsRequest?.isLoaded) {
    allHoldingsData = _get(useAllHoldingsRequest, 'data.allHoldings', []);
  }
  const allHoldingsAsOfDate =
    allHoldingsData && allHoldingsData.length ? allHoldingsData[0].AsofDate : 32768;

  const handleSort = currentSortBy => {
    if (useAllHoldingsRequest.isLoading || currentSortBy === 'HoldingTicker') return; // If we send another sort request right after the first one, it will error out, this is used to prevent it; QID isn't able to sort by HoldingTicker.
    if (currentSortBy !== sortBy) {
      setSortBy(currentSortBy);
      setSortDir('desc');
    } else {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    }
  };
  // eslint-disable-next-line no-shadow
  const sortArrow = (sortBy, sortDir, tableHeadersRawName) => {
    if (sortBy === tableHeadersRawName) {
      return sortDir === 'desc' ? 'headerSortDown' : 'headerSortUp';
    }
    return tableHeadersRawName === 'HoldingTicker' ? '' : 'headerSortDefault';
  };
  // Pagination array
  const itemsPerPage = 30;
  const [currentPageNumber, setcurrentPageNumber] = useState(1);
  const [dividedHoldingsArray, setDividedHoldingsArray] = useState([]);
  const [currentHoldings, setCurrentHoldings] = useState([]);

  useEffect(() => {
    if (!allHoldingsData.length && !dividedHoldingsArray.length) {
      return;
    }
    const dividedHoldings = [];
    for (let i = 0; i < allHoldingsData.length; i += itemsPerPage) {
      const chunk = allHoldingsData.slice(i, i + itemsPerPage);
      dividedHoldings.push(chunk);
    }
    setDividedHoldingsArray(dividedHoldings);
    setCurrentHoldings(dividedHoldings[currentPageNumber - 1]);
  }, [allHoldingsData?.length, sortDir, sortBy, allHoldingsData[0]?.HoldingName]);

  useEffect(() => {
    if (!dividedHoldingsArray.length) {
      return;
    }
    setCurrentHoldings(dividedHoldingsArray[currentPageNumber - 1]);
  }, [currentPageNumber]);

  const handlePageChange = pageIndex => {
    setcurrentPageNumber(pageIndex);
  };

  return (
    <CardModule
      id={'detailedHoldings'}
      data-analytics-componenttype="full-width-component"
      data-analytics-id="FW_InvestmentHoldings"
      data-analytics-section="component"
    >
      <CardModule.Header>
        Investment Holdings
        <CardModule.AsOfDate>{format.date(asOfDate)}</CardModule.AsOfDate>
      </CardModule.Header>
      <CardModule.Body {...className('table-scroll')}>
        <TableBasic flush>
          <caption {...className('sr-only')}>Top Ten Holdings Data</caption>
          <thead>
            <tr>
              <th {...className('text-nowrap', 'w-100')}>
                <span aria-hidden="true" {...className('text-left', 'pl-4')}>
                  Top Ten Holdings
                </span>
                <span {...className('text-left', 'sr-only')}>Symbol</span>
              </th>
              <th {...className('text-right', 'text-nowrap', 'pr-4')}>Market Value (USD $)</th>
              <th {...className('text-right', 'text-nowrap', 'pr-4')}>Portfolio Weight (%)</th>
            </tr>
          </thead>
          <tbody>
            {holdingsOrdered.map((holding, index) => (
              <tr
                data-key={`${holding.name || ''}-${index}`}
                key={`${holding.name || ''}-${index}`}
                {...className(index === holdingsOrdered.length - 1 ? 'border-bottom' : '')}
              >
                <th scope="row" {...className('text-left', 'pl-4')}>
                  {format.text(holding.name)}
                </th>
                <td {...className('text-right', 'pr-4', 'font-weight-bold')}>
                  {holding.marketValue
                    ? format.price(holding.marketValue, { formatString: '$0.0a' }, true)
                    : '--'}
                </td>
                <td {...className('text-right', 'pr-4', 'font-weight-bold')}>
                  {holding.weightPercent ? format.percent(holding.weightPercent) : '--'}
                </td>
              </tr>
            ))}
          </tbody>
        </TableBasic>
      </CardModule.Body>
      <CardModule.Footer>
        <div {...className('d-flex', 'justify-content-between')}>
          {detailedHoldingsUrl && (
            <>
              <Button
                variant={'outline-black'}
                {...className(
                  'btn--rounded',
                  'font-weight-bold',
                  'text-uppercase',
                  'mt-2',
                  'd-print-none'
                )}
                size="sm"
                role="button"
                onClick={handleShow}
              >
                View Detailed Holdings
              </Button>
              {/* Skipping this for now per notes in TIAA-7971
              <CardModule.AsOfDate>
                {format.date(asOfDate)}
              </CardModule.AsOfDate> */}
              {/* May need to update date with it's own asOfDate variable, as it could be different than the date for the table. Unsure until we see the data  */}
            </>
          )}
          {!isFundNonProp && <ButtonDisclosures module={'investment-holdings'} />}
        </div>
      </CardModule.Footer>
      <ModalBasic show={isModalVisible} onHide={handleClose} className={'allHoldings'}>
        <ModalBasic.Header>
          <ModalBasic.Title>
            INVESTMENT HOLDINGS
            <div
              {...className(
                'ml-2',
                'font-weight-normal',
                'font-size-12',
                'text-white',
                'd-block',
                'd-md-inline',
                'all-holdings-asofdate'
              )}
            >
              {' '}
              {`As of ${format.oaDate(allHoldingsAsOfDate, { formatString: 'MM/DD/YYYY' })}`}{' '}
            </div>
            <ModalBasic.Subtitle>{_get(commonState, 'xref.data.name')}</ModalBasic.Subtitle>
          </ModalBasic.Title>
          <ModalBasic.CloseButton onClick={handleClose} />
        </ModalBasic.Header>

        <ModalBasic.Body>
          <TableBasic flush>
            <thead>
              <tr>
                {getTableHeader(_get(commonState, 'assetClass')).map((item, index) => (
                  <th
                    data-key={`${item || ''}-${index}`}
                    key={`${item || ''}-${index}`}
                    {...className(
                      'text-nowrap',
                      item === 'Ticker' || item === 'Holding Name' ? 'text-left' : 'text-right',
                      sortArrow(sortBy, sortDir, tableHeadersRawName[index])
                    )}
                    onClick={() => handleSort(tableHeadersRawName[index])}
                  >
                    {item}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentHoldings?.map((item, index) =>
                getTableRow(_get(commonState, 'assetClass'))(item, index)
              )}
            </tbody>
          </TableBasic>

          <Pagination
            dividedArray={dividedHoldingsArray}
            currentSection={currentHoldings}
            currentPageNumber={currentPageNumber}
            handlePageChange={handlePageChange}
          ></Pagination>
        </ModalBasic.Body>

        <ModalBasic.Footer>
          The Weight (%) column will not sum to 100%, due to cash, other assets & liabilities being
          excluded from the list.
        </ModalBasic.Footer>
      </ModalBasic>
    </CardModule>
  );
};

InvestmentHoldings.defaultProps = {
  holdings: []
};

InvestmentHoldings.propTypes = {
  isFundNonProp: PropTypes.bool,
  holdings: PropTypes.arrayOf(
    PropTypes.shape({
      asOfDate: PropTypes.string,
      name: PropTypes.string,
      marketValue: PropTypes.number,
      weightPercent: PropTypes.number
    })
  ),
  // asOfDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  detailedHoldingsUrl: PropTypes.string
};

export default InvestmentHoldings;
